import React, { useRef, useEffect, useState } from 'react';

interface SingleRangeSliderProps {
  min: number;
  max: number;
  value: number | string;
  onChange: (value: number) => void;
  label?: React.ReactNode;
  unit?: string;
  disabled?: boolean;
  colorTheme?: 'primary' | 'accent';
}

const SingleRangeSlider: React.FC<SingleRangeSliderProps> = ({
  min,
  max,
  value,
  onChange,
  label,
  unit = 'RM',
  disabled = false,
  colorTheme = 'accent',
}) => {
  const sliderRef = useRef<HTMLInputElement>(null);
  const rangeRef = useRef<HTMLDivElement>(null);

  const numValue = Number(value);
  const colorClass = colorTheme === 'primary' ? 'primary' : 'accent';
  const inputFocusClass = colorTheme === 'primary' ? 'focus:border-primary' : 'focus:border-accent';
  const rangeFillClass = colorTheme === 'primary' ? 'bg-primary' : 'bg-accent';
  const rangeAccentClass = colorTheme === 'primary' ? 'accent-primary' : 'accent-accent';
  const sliderStyle = {
    '--slider-thumb': `rgb(var(--brand-${colorClass}))`,
    '--slider-thumb-hover': `rgb(var(--brand-${colorClass}-600))`,
  } as React.CSSProperties;
  const [inputValue, setInputValue] = useState(String(numValue));

  useEffect(() => {
    setInputValue(String(numValue));
  }, [numValue]);

  useEffect(() => {
    if (rangeRef.current) {
      const percent = ((numValue - min) / (max - min)) * 100;
      rangeRef.current.style.right = `${100 - percent}%`;
    }
  }, [numValue, min, max]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const val = Number(e.target.value);
    onChange(val);
    setInputValue(String(val));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    if (disabled) return;
    const parsed = parseInt(inputValue, 10);
    const clamped = isNaN(parsed) ? min : Math.min(max, Math.max(min, parsed));
    onChange(clamped);
    setInputValue(String(clamped));
  };

  return (
    <div className="single-range-slider w-full" style={sliderStyle}>
      {label && (
        <label className={`block text-sm font-medium mb-2 ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
          {label}
        </label>
      )}

      <div className="flex items-center gap-1 mb-4">
        <span className={`text-sm font-semibold ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>
          {unit}
        </span>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          className={`w-20 text-sm font-semibold bg-transparent border-b text-center outline-none transition-colors ${
            disabled
              ? 'text-gray-400 border-transparent cursor-not-allowed'
              : `text-gray-900 border-gray-300 ${inputFocusClass}`
          }`}
        />
      </div>

      <div className={`relative pt-2 pb-2 ${disabled ? 'cursor-not-allowed' : ''}`}>
        <div className={`absolute top-1/2 transform -translate-y-1/2 w-full h-2 rounded-lg pointer-events-none ${disabled ? 'bg-gray-200' : 'bg-gray-300'}`} />
        <div
          ref={rangeRef}
          className={`absolute top-1/2 transform -translate-y-1/2 left-0 h-2 rounded-lg pointer-events-none ${disabled ? 'bg-gray-300' : rangeFillClass}`}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={numValue}
          onChange={handleSliderChange}
          disabled={disabled}
          ref={sliderRef}
          className={`absolute w-full h-2 top-1/2 transform -translate-y-1/2 appearance-none bg-transparent z-10 ${rangeAccentClass}`}
          style={{ WebkitAppearance: 'none', cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
      </div>

      <style>{`
        .single-range-slider input[type='range']::-webkit-slider-thumb {
          appearance: none;
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--slider-thumb);
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          pointer-events: auto;
          z-index: 10;
        }
        .single-range-slider input[type='range']:disabled::-webkit-slider-thumb {
          background: #cbd5e1;
          cursor: not-allowed;
          pointer-events: none;
          box-shadow: none;
        }
        .single-range-slider input[type='range']::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--slider-thumb);
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          pointer-events: auto;
          z-index: 10;
        }
        .single-range-slider input[type='range']:disabled::-moz-range-thumb {
          background: #cbd5e1;
          cursor: not-allowed;
          pointer-events: none;
          box-shadow: none;
        }
        .single-range-slider input[type='range']::-webkit-slider-thumb:hover {
          background: var(--slider-thumb-hover);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }
        .single-range-slider input[type='range']:disabled::-webkit-slider-thumb:hover {
          background: #cbd5e1;
          box-shadow: none;
        }
        .single-range-slider input[type='range']::-moz-range-thumb:hover {
          background: var(--slider-thumb-hover);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }
        .single-range-slider input[type='range']:disabled::-moz-range-thumb:hover {
          background: #cbd5e1;
          box-shadow: none;
        }
      `}</style>
    </div>
  );
};

export default SingleRangeSlider;
